import { useEffect, useState } from "react";
import { Bell, BellOff, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  isPushSupported, subscribeToPush, unsubscribeFromPush, currentSubscriptionStatus,
} from "@/lib/push";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

export default function NotificationCenter({ accentColor = "#0F172A" }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [status, setStatus] = useState({ supported: false, subscribed: false, permission: "default" });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications/me");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) return;
    refresh();
    currentSubscriptionStatus().then(setStatus);
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [user]);

  const onOpen = () => {
    if (unread > 0) {
      api.post("/notifications/mark-read").then(() => { setUnread(0); refresh(); }).catch(() => {});
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      const s = await currentSubscriptionStatus();
      setStatus(s);
      toast.success("Notifications enabled");
    } catch (e) { toast.error(e.message || apiErr(e)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      const s = await currentSubscriptionStatus();
      setStatus(s);
      toast.success("Notifications disabled");
    } catch (e) { toast.error(e.message || apiErr(e)); }
    finally { setBusy(false); }
  };

  const test = async () => {
    try {
      await api.post("/notifications/test", { title: "Test ping", body: "If you can read this, notifications work!" });
      toast.success("Test sent (check the bell)");
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!user) return null;

  return (
    <DropdownMenu onOpenChange={(v) => v && onOpen()}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notif-bell-btn" aria-label="Notifications">
          <Bell className="h-5 w-5" style={{ color: accentColor }} />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 text-[10px] rounded-full px-1.5 py-0.5 font-bold text-white"
              style={{ background: accentColor }}
              data-testid="notif-unread-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {status.supported && (
            status.subscribed ? (
              <Button size="sm" variant="ghost" disabled={busy} onClick={disable} data-testid="notif-disable-btn" className="h-6 text-xs px-2">
                <BellOff className="h-3 w-3 mr-1" /> Disable
              </Button>
            ) : (
              <Button size="sm" variant="ghost" disabled={busy} onClick={enable} data-testid="notif-enable-btn" className="h-6 text-xs px-2">
                <Bell className="h-3 w-3 mr-1" /> Enable push
              </Button>
            )
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-72 overflow-y-auto">
          {items.length === 0 && (
            <p className="px-4 py-6 text-sm text-slate-400 text-center" data-testid="notif-empty">No notifications yet.</p>
          )}
          {items.map((n) => (
            <div key={n.id} className="px-3 py-2 border-b border-slate-100 last:border-0" data-testid={`notif-item-${n.id}`}>
              <div className="text-sm font-medium text-slate-900 flex items-start gap-2">
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />}
                <span>{n.title}</span>
              </div>
              <div className="text-xs text-slate-600 ml-3">{n.body}</div>
              <div className="text-[10px] text-slate-400 mt-0.5 ml-3">{formatDateTime(n.created_at)}</div>
            </div>
          ))}
        </div>
        {status.supported && status.subscribed && (
          <>
            <DropdownMenuSeparator />
            <div className="px-3 py-2">
              <Button size="sm" variant="outline" className="w-full" onClick={test} data-testid="notif-test-btn">
                <Check className="h-3 w-3 mr-2" /> Send test notification
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
